import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Target, RefreshCw, ChevronDown, ChevronRight, ArrowRight,
  ShieldCheck, Lock, Search, RotateCw, Scale, Activity, Wrench, Zap, ArrowUpCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import RelatedItems from '../components/RelatedItems'
import KpiTile from '../components/KpiTile'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'
import PageLoading from '../components/PageLoading'
import EmptyState from '../components/EmptyState'

type Action = {
  id: string
  rank?: number
  title: string
  why_now: string
  category: string
  domain?: string
  urgency_score: number
  time_criticality: string
  blast_radius: number
  deep_link: string
  effort: string
  source: string
  sources?: string[]
  evidence?: Record<string, unknown>
  llm_polished?: boolean
}

type Summary = {
  total: number
  by_category: Record<string, number>
  by_source: Record<string, number>
  by_domain?: Record<string, number>
  top_3_titles: string[]
  highest_urgency: number
  closing_fastest: string | null
  patch_count: number
  comply_count: number
  generated_at: string
}

// Domain lenses — All + the four scoped views. `null` key = All.
const DOMAINS: { key: string | null; label: string }[] = [
  { key: null, label: 'All' },
  { key: 'cyber', label: 'Cyber' },
  { key: 'ai', label: 'AI' },
  { key: 'infra', label: 'Infra' },
  { key: 'cdm', label: 'CDM' },
]

const CATEGORY_ICON: Record<string, React.ElementType> = {
  patch: Wrench, contain: ShieldCheck, review: Search, rotate: RotateCw,
  comply: Scale, investigate: Activity, harden: Lock, upgrade: ArrowUpCircle,
}

const CATEGORY_COLOR: Record<string, string> = {
  patch: 'text-amber-light border-amber/30 bg-amber/10',
  contain: 'text-rose-light border-rose/30 bg-rose/10',
  review: 'text-blue-light border-blue/30 bg-blue/10',
  rotate: 'text-cyan-light border-cyan/30 bg-cyan/10',
  comply: 'text-purple-light border-purple/30 bg-purple/10',
  investigate: 'text-rose-light border-rose/30 bg-rose/10',
  harden: 'text-emerald-light border-emerald/30 bg-emerald/10',
  upgrade: 'text-amber-light border-amber/30 bg-amber/10',
}

const CATEGORIES = ['patch', 'contain', 'comply', 'investigate', 'rotate', 'harden', 'review', 'upgrade']
const SOURCES = ['exploitation_race', 'kev', 'ai_reasoner', 'toxic_combo', 'incident',
  'regulation_timer', 'credential_expiry', 'underwriting', 'ncsc', 'ai_toxic_combo',
  'aisi_eval', 'ai_discovered_vuln', 'eol', 'sbom', 'cloud_status', 'dora',
  'cdm_orphan', 'cdm_policy_proposal', 'cdm_change', 'cdm_adapter', 'cdm_conflict']

function urgencyTone(u: number): string {
  if (u >= 85) return 'bg-rose'
  if (u >= 65) return 'bg-amber'
  if (u >= 40) return 'bg-blue'
  return 'bg-emerald'
}

// A time_criticality is "imminent" (red badge) when it talks about hours,
// being underway/overdue/expired, or a sub-1-day window.
function isImminent(tc: string): boolean {
  const s = (tc || '').toLowerCase()
  return /\b\d+h\b|<1h|<1d|underway|overdue|expired|now\b|imminent|in <|in 0|negative/.test(s)
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
        active ? 'border-cyan/50 bg-cyan/15 text-cyan-light'
          : 'border-border bg-surface-raised text-text-muted hover:text-text-secondary',
      )}
    >
      {children}
    </button>
  )
}

function ActionCard({ action }: { action: Action }) {
  const [open, setOpen] = useState(false)
  const Icon = CATEGORY_ICON[action.category] || Target
  const catCls = CATEGORY_COLOR[action.category] || 'text-text-secondary border-border bg-surface-raised'
  const imminent = isImminent(action.time_criticality)
  const sources = action.sources && action.sources.length ? action.sources : [action.source]
  return (
    <div className="card p-3">
      <div className="flex items-start gap-3">
        {/* Rank */}
        <div className="flex flex-col items-center pt-0.5 w-8 flex-shrink-0">
          <div className="text-lg font-bold text-text-primary leading-none">{action.rank}</div>
          <div className="text-[8px] uppercase tracking-widest text-text-muted">rank</div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 whitespace-nowrap', catCls)}>
                <Icon size={10} /> {action.category}
              </span>
              <span className="text-sm font-semibold text-text-primary truncate">{action.title}</span>
            </div>
            <span className={clsx(
              'text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap flex-shrink-0',
              imminent ? 'border-rose/50 bg-rose/15 text-rose-light font-semibold'
                : 'border-border bg-surface-raised text-text-muted',
            )}>
              {action.time_criticality}
            </span>
          </div>

          {/* Urgency bar */}
          <div className="flex items-center gap-2 mt-2">
            <div className="h-1.5 flex-1 rounded-full bg-surface-raised overflow-hidden">
              <div className={clsx('h-full rounded-full', urgencyTone(action.urgency_score))}
                   style={{ width: `${Math.max(2, Math.min(100, action.urgency_score))}%` }} />
            </div>
            <span className="text-[10px] font-mono text-text-muted w-8 text-right">{Math.round(action.urgency_score)}</span>
          </div>

          {/* Why now */}
          <p className="text-[12px] text-text-secondary mt-2">{action.why_now}</p>

          {/* Source chips + effort + act */}
          <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
            <div className="flex items-center gap-1 flex-wrap">
              {sources.map(s => (
                <span key={s} className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-surface-raised text-text-muted whitespace-nowrap">
                  {s}
                </span>
              ))}
              <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-surface-raised text-text-muted whitespace-nowrap">
                effort: {action.effort}
              </span>
              {action.llm_polished && (
                <span className="text-[9px] px-1.5 py-0.5 rounded border border-purple/30 bg-purple/10 text-purple-light whitespace-nowrap inline-flex items-center gap-1">
                  <Zap size={8} /> ai
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setOpen(o => !o)}
                      className="text-[10px] text-text-muted hover:text-text-secondary inline-flex items-center gap-0.5">
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} evidence
              </button>
              <Link to={action.deep_link}
                    className="text-[11px] font-medium text-cyan-light hover:underline inline-flex items-center gap-1">
                Act <ArrowRight size={12} />
              </Link>
            </div>
          </div>

          {/* Evidence expand */}
          {open && (
            <>
              <pre className="mt-2 text-[10px] text-text-muted bg-surface-base border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(action.evidence || {}, null, 2)}
              </pre>
              <RelatedItems entityType="nba_action" entityKey={action.id} className="mt-2" />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function NextBestActionPage() {
  const [items, setItems] = useState<Action[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [running, setRunning] = useState(false)
  const [catFilter, setCatFilter] = useState<string | null>(null)
  const [srcFilter, setSrcFilter] = useState<string | null>(null)
  const [domainFilter, setDomainFilter] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get('/api/next-best-action', { params: { limit: 50 } }),
        api.get('/api/next-best-action/summary'),
      ])
      setItems((listRes.data.items || []) as Action[])
      setSummary(sumRes.data as Summary)
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const recompute = async () => {
    setRunning(true); setMsg(''); setError('')
    try {
      const r = await api.post('/api/next-best-action/recompute')
      const d = r.data as { cached?: number }
      setMsg(`Recomputed — ${d.cached ?? 0} actions cached.`)
      await load()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      setError(status === 401 ? 'Admin role required to recompute.' : 'Recompute failed.')
    } finally {
      setRunning(false)
    }
  }

  // Per-domain counts come from the full (unfiltered) list so each tab shows
  // its own total regardless of the active tab. Fall back to the summary's
  // by_domain if the list is empty.
  const domainCount = (key: string | null): number => {
    if (key === null) return items.length
    const fromItems = items.filter(a => (a.domain || 'cyber') === key).length
    if (fromItems > 0 || items.length > 0) return fromItems
    return summary?.by_domain?.[key] ?? 0
  }

  const filtered = items.filter(a => {
    if (domainFilter && (a.domain || 'cyber') !== domainFilter) return false
    if (catFilter && a.category !== catFilter) return false
    if (srcFilter && !(a.sources || [a.source]).includes(srcFilter)) return false
    return true
  })

  const activeDomainLabel = (DOMAINS.find(d => d.key === domainFilter)?.label || 'this domain').toLowerCase()

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <PageHeader
        icon={Target} iconClassName="text-cyan" title="NEXT BEST ACTION"
        subtitle="what to do right now, ranked — time-criticality wins"
        actions={<>
          {msg && <span className="text-[11px] text-emerald-light">{msg}</span>}
          <button type="button" onClick={() => void recompute()} disabled={running}
                  className="btn-ghost">
            <RefreshCw size={14} className={running ? 'animate-spin' : ''} /> Recompute
          </button>
        </>}
      />

      <ErrorBanner message={error} />

      {/* Domain tabs */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-border pb-2">
        {DOMAINS.map(d => {
          const active = domainFilter === d.key
          const count = domainCount(d.key)
          return (
            <button
              key={d.label}
              type="button"
              onClick={() => setDomainFilter(d.key)}
              className={clsx(
                'text-xs px-3 py-1 rounded-md border transition-colors inline-flex items-center gap-1.5',
                active
                  ? 'border-cyan/50 bg-cyan/15 text-cyan-light font-semibold'
                  : 'border-border bg-surface-raised text-text-muted hover:text-text-secondary',
              )}
            >
              {d.label}
              <span className={clsx(
                'text-[10px] px-1.5 rounded-full',
                active ? 'bg-cyan/25 text-cyan-light' : 'bg-surface-base text-text-muted',
              )}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total actions" value={summary?.total ?? '—'} />
        <KpiTile label="Closing fastest"
              value={<span className="text-sm">{summary?.closing_fastest || '—'}</span>}
              tone={summary?.closing_fastest && isImminent(summary.closing_fastest) ? 'text-rose-light' : undefined} />
        <KpiTile label="Patch actions" value={summary?.patch_count ?? '—'} tone="text-amber-light" />
        <KpiTile label="Compliance" value={summary?.comply_count ?? '—'} tone="text-purple-light" />
      </div>

      {/* Filters */}
      <div className="card p-3 space-y-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-text-muted mr-1">category</span>
          <Chip active={catFilter === null} onClick={() => setCatFilter(null)}>all</Chip>
          {CATEGORIES.map(c => (
            <Chip key={c} active={catFilter === c} onClick={() => setCatFilter(catFilter === c ? null : c)}>{c}</Chip>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-text-muted mr-1">source</span>
          <Chip active={srcFilter === null} onClick={() => setSrcFilter(null)}>all</Chip>
          {SOURCES.map(s => (
            <Chip key={s} active={srcFilter === s} onClick={() => setSrcFilter(srcFilter === s ? null : s)}>{s}</Chip>
          ))}
        </div>
      </div>

      {/* Ranked list */}
      {loading ? (
        <PageLoading label="Synthesising signals…" />
      ) : filtered.length === 0 ? (
        <EmptyState icon={ShieldCheck} tone="text-emerald-light"
          title={items.length === 0
            ? "Nothing urgent right now — you're on top of it."
            : domainFilter
              ? `Nothing urgent in ${activeDomainLabel} right now.`
              : 'No actions match the filters.'}
          hint={items.length === 0 ? 'Actions appear here as exploitation races, deadlines, and incidents emerge.' : 'Clear a filter to see more.'} />
      ) : (
        <div className="space-y-2">
          {filtered.map(a => <ActionCard key={a.id} action={a} />)}
        </div>
      )}
    </div>
  )
}
